import type { Meeting } from '@nexo/types';
import { api } from '../../lib/api';
import { formatDay, formatTime } from '../../lib/format';
import { t } from '../../lib/i18n';
import { isDesktop, openExternal } from '../../lib/platform';

/** Joinable from 10 minutes before the start until the end (plus a grace hour). */
export function meetingState(
  meeting: Meeting,
  now = new Date(),
): 'upcoming' | 'live' | 'ended' | 'cancelled' {
  if (meeting.cancelledAt) return 'cancelled';
  const start = new Date(meeting.startsAt).getTime();
  const end = new Date(meeting.endsAt).getTime();
  if (now.getTime() < start - 10 * 60_000) return 'upcoming';
  if (now.getTime() <= end + 60 * 60_000) return 'live';
  return 'ended';
}

/** «Hoy · 10:00 – 10:45». */
export function formatMeetingTime(meeting: Pick<Meeting, 'startsAt' | 'endsAt'>): string {
  return `${formatDay(meeting.startsAt)} · ${formatTime(meeting.startsAt)} – ${formatTime(meeting.endsAt)}`;
}

const gcalStamp = (iso: string) => iso.replace(/[-:]/g, '').replace(/\.\d{3}/, '');

export function googleCalendarUrl(meeting: Meeting, joinUrl: string): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: meeting.title,
    dates: `${gcalStamp(meeting.startsAt)}/${gcalStamp(meeting.endsAt)}`,
    details: `${meeting.description ? `${meeting.description}\n\n` : ''}${t('Unirse: {{url}}', { url: joinUrl })}`,
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** Downloads the .ics (web) or opens Google Calendar (desktop webviews can't save files). */
export async function addToCalendar(meeting: Meeting, joinUrl: string): Promise<void> {
  if (isDesktop) {
    await openExternal(googleCalendarUrl(meeting, joinUrl));
    return;
  }
  const text = await api.meetings.ics(meeting.id);
  const url = URL.createObjectURL(new Blob([text], { type: 'text/calendar' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `${meeting.title.replace(/[^\w\- ]+/g, '').trim() || 'reunion'}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Link to the meeting inside Nexo (for calendars). */
export function meetingLink(meeting: Meeting): string {
  const origin = window.location.origin.startsWith('http')
    ? window.location.origin
    : 'https://nexo-hub.dev';
  return `${origin}/app/t/${meeting.teamId}/meetings?meeting=${meeting.id}`;
}
